/**
 * AUTO-GENERATED — DO NOT EDIT.
 * Source: sdk.manifest.yml + openapi.json.
 * Regenerate with: pnpm gen
 */


import { type QueryFunctionContext } from '@tanstack/react-query';

import { recordSnapshotLastSeq } from '../../nats/snapshot-seq-store.js';

import { fetchWithHttpTelemetry } from '../../transport/http-request.js';

import { throwHttpError } from '../../core/http-error.js';

import type { GetExchangeRatesRow } from '../openapi-types.gen.js';

import type { EntityKeyFactory, HookSpec } from '../../types/HookSpec.js';
import type { JsonSerialisableArgs } from '../../types/JsonSerialisableArgs.js';


// ─── useGetExchangeRates ────────────────────────────────────────────────────────────

export interface useGetExchangeRatesArgs { readonly fields?: readonly ('base' | 'rates' | 'updatedAt')[]; }


async function _useGetExchangeRatesQueryFn(
  args: useGetExchangeRatesArgs,
  ctx: QueryFunctionContext,
): Promise<GetExchangeRatesRow | null> {
  const url = '/v1/exchange-rates';
  const params = new URLSearchParams();
  if (args.fields !== undefined) params.set('fields', args.fields.join(','));
  const sep = url.includes('?') ? '&' : '?';
  const full = params.size > 0 ? `${url}${sep}${params.toString()}` : url;
  const res = await fetchWithHttpTelemetry({
    operationName: 'useGetExchangeRates',
    routeTemplate: '/v1/exchange-rates',
    requestKind: 'query',
    method: 'GET',
    url: full,
    signal: ctx.signal,
    init: {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
      ...(ctx.signal !== undefined ? { signal: ctx.signal } : {}),
    },
  });
  // A signed-out / session-expired caller gets 401 — that is a normal
  // "anonymous" state. A 403 retains its canonical error envelope.
  if (res.status === 401) return null;
  if (!res.ok) await throwHttpError('GET', full, res);
  const json = (await res.json()) as unknown;
  recordSnapshotLastSeq(ctx.queryKey, json);
  const data =
    json !== null && typeof json === 'object' && 'data' in json
      ? (json as { data: unknown }).data
      : json;
  return data as GetExchangeRatesRow;
}


const _useGetExchangeRatesFactory = Object.assign(
  (args: useGetExchangeRatesArgs) => ({
    queryKey: ['exchange-rates', 'detail', args.fields ?? []] as readonly unknown[],
    queryFn: (ctx: QueryFunctionContext) => _useGetExchangeRatesQueryFn(args, ctx),
    staleTime: 60000,
    // structuralSharing kept ON (TanStack default). The codegen-emitted
    // applyEvent already preserves row references for unchanged rows;
    // structural sharing on top lets per-card `select` selectors
    // short-circuit when their row hasn't changed, avoiding cascade
    // re-renders of every list child on each patch.
    meta: {
      realtime: false,
      pollingManaged: true,
      specName: 'useGetExchangeRates',
      viewerSensitive: false,
      persist: true,
    } as const,
  }),
  {
    __brand: 'EntityKeyFactory' as const,
    prefix: () => ['exchange-rates', 'detail'] as readonly unknown[],
  },
) as unknown as EntityKeyFactory<useGetExchangeRatesArgs, GetExchangeRatesRow>;


export const useGetExchangeRatesSpec = {
  __brand: 'HookSpec' as const,
  path: '/v1/exchange-rates',
  method: 'GET' as const,
  kind: 'detail' as const,
  mounting: { kind: 'singleton' as const, allowsPerEntity: false },
  entityKeys: { detail: _useGetExchangeRatesFactory },
  parseInput: (raw: unknown): useGetExchangeRatesArgs => raw as useGetExchangeRatesArgs,
  parseResponse: (raw: unknown): GetExchangeRatesRow => raw as GetExchangeRatesRow,
  specName: 'useGetExchangeRates' as const,
  polling: { hasNats: false, intervalMs: 60000 } as const,
  viewerSensitive: false as const,
  nonpersistent: false as const,
} as unknown as HookSpec<JsonSerialisableArgs, GetExchangeRatesRow>;
